import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useBiens } from "@/hooks/useBiens";
import type { Bien } from "@/types/bien";
import { Printer, X } from "lucide-react";
import srbLogo from "@/assets/SRB.jpg";
import { dispoCell } from "@/lib/installationDisplay";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const today = new Date();

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/** Rouge éditorial du mailing (lignes promo + "NOW"). */
const RED = "#FF0000";
/** Bleu de la légende "First occupation". */
const BLUE = "#1D4ED8";

function parseDispoDate(iso: string | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Nombre de jours depuis que le bien est disponible (seraDispoPour passé).
 * 0 si la date est dans le futur ou absente.
 */
function daysAvailable(iso: string | undefined): number {
  const d = parseDispoDate(iso);
  if (!d || d > today) return 0;
  return Math.floor((today.getTime() - d.getTime()) / 86400000);
}

/**
 * Cellule "Available" :
 *  - date passée ou absente → "NOW" (now = true)
 *  - date future → "dd-mon-yy"
 */
function formatAvailable(iso: string | undefined): { label: string; now: boolean } {
  const d = parseDispoDate(iso);
  if (!d || d <= today) return { label: "NOW", now: true };
  const day = String(d.getDate()).padStart(2, "0");
  const yr = String(d.getFullYear()).slice(2);
  return { label: `${day}-${MONTHS[d.getMonth()]}-${yr}`, now: false };
}

function formatHeaderDate(d: Date): string {
  return `${d.getDate()}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function fPrice(val: string | undefined): string {
  if (!val) return "—";
  const n = parseFloat(val);
  if (isNaN(n)) return val;
  return `€ ${n.toLocaleString("fr-BE")}`;
}

/** Abréviations des charges incluses (ordre canonique du document). */
const CHARGE_ABBR: Record<string, string> = {
  "Charges communes": "CC",
  "Eau froide": "CW",
  "Eau chaude": "HW",
  Chauffage: "H",
  Electricité: "Elec",
  TV: "TV",
  Internet: "Int",
  Assurance: "Ins",
};
const CHARGE_ORDER = ["CC", "CW", "HW", "H", "Elec", "TV", "Int", "Ins"];

function formatCharges(list: string[] | undefined): string {
  if (!list || list.length === 0) return "—";
  const abbrs = list.map((c) => CHARGE_ABBR[c] ?? c);
  abbrs.sort((a, b) => {
    const ia = CHARGE_ORDER.indexOf(a);
    const ib = CHARGE_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  return abbrs.join(", ");
}

// En-têtes des 17 colonnes principales (avant les 2 colonnes énergie).
const MAIN_HEADERS = [
  "Apartment",
  "Project",
  "Community",
  "Nbr. Rooms",
  "Furnished",
  "Rental Price",
  "Available",
  "Floor",
  "Bathroom",
  "Type Floor",
  "Cellar",
  "Parking",
  "Rental garage",
  "Charges",
  "Charges include",
  "Company Contract",
  "Address",
];

// Largeurs FIXES des colonnes (table-layout: fixed), dans l'ordre exact des
// colonnes : 17 colonnes principales, la gouttière, puis les 2 colonnes énergie.
// Le navigateur conserve ces ratios (mise à l'échelle proportionnelle sur 100%).
const COL_WIDTHS = [
  56, //  1 Apartment
  96, //  2 Project       (tient sur 1 ligne)
  62, //  3 Community     (tient sur 1 ligne)
  24, //  4 Nbr. Rooms
  24, //  5 Furnished
  42, //  6 Rental Price
  50, //  7 Available
  24, //  8 Floor
  24, //  9 Bathroom
  48, // 10 Type Floor
  60, // 11 Cellar       (logique non / incl.(n°) / oui(montant) / poss(montant))
  60, // 12 Parking
  64, // 13 Rental garage
  42, // 14 Charges
  80, // 15 Charges include (≈ 2 lignes)
  30, // 16 Company Contract
  104, // 17 Address
  12, // (gouttière)
  32, // Consommation par m²
  30, // Classement énergétique
];

// ─── Component ───────────────────────────────────────────────────────────────

export function MailingPage() {
  const { t } = useTranslation();
  const { data: biensData } = useBiens();
  const allBiens = useMemo(() => (biensData ?? []) as unknown as Bien[], [biensData]);

  // IDs passés depuis la liste (Apparts for Rent V1/V2) via localStorage.
  const storedIds: string[] = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("mailing-bien-ids") || "[]");
    } catch {
      return [];
    }
  }, []);

  // Préserve l'ordre trié de la liste source.
  const mailingBiens = useMemo(() => {
    if (storedIds.length === 0) return allBiens;
    const byId = new Map(allBiens.map((b) => [b.id, b]));
    return storedIds.map((id) => byId.get(id)).filter(Boolean) as typeof allBiens;
  }, [allBiens, storedIds]);

  // Groupé par projet, ordre préservé.
  const groups = useMemo(() => {
    const map = new Map<string, typeof mailingBiens>();
    for (const b of mailingBiens) {
      const key = b.projet || "(Sans projet)";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(b);
    }
    return Array.from(map.entries());
  }, [mailingBiens]);

  return (
    <>
      {/* Print CSS */}
      <style>{`
        .mailing-root * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .vth {
          height: 128px;
          vertical-align: middle;
          text-align: center;
          padding: 4px 2px;
        }
        .vth > span {
          writing-mode: vertical-rl;
          transform: rotate(180deg);
          display: inline-block;
          white-space: nowrap;
          font-weight: bold;
          font-size: 10px;
          line-height: 1.1;
        }
        .vth.wrap > span { white-space: normal; max-height: 122px; }
        @media print {
          .no-print { display: none !important; }
          @page { size: A4 landscape; margin: 8mm 8mm 13mm 8mm; }
          body { font-size: 8pt; }
          .mailing-footer { position: fixed; bottom: 0; left: 0; right: 0; }
        }
      `}</style>

      <div
        className="mailing-root bg-white min-h-screen text-black"
        style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: "9px" }}
      >
        {/* Toolbar — masquée à l'impression */}
        <div className="no-print sticky top-0 bg-gray-50 border-b border-gray-200 px-6 py-2 flex items-center gap-3 z-20 shadow-sm">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-1.5 bg-gray-900 text-white rounded text-sm font-medium hover:bg-gray-700 cursor-pointer transition-colors"
          >
            <Printer size={14} />
            {t("mailing.printExport")}
          </button>
          <button
            onClick={() => window.close()}
            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded text-sm font-medium hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <X size={14} />
            {t("mailing.close")}
          </button>
          <span className="ml-auto text-sm text-gray-500">
            {mailingBiens.length} {t("mailing.apartments")} — {formatHeaderDate(today)}
          </span>
        </div>

        <div className="p-6">
          {/* ── Header (2 colonnes : logo+contacts à gauche, titre+légende au centre) ── */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: "64px", marginBottom: "10px" }}>
            {/* Colonne gauche : logo + contacts */}
            <div style={{ flexShrink: 0 }}>
              <img src={srbLogo} alt="Skyline Renting Services" style={{ height: "82px", objectFit: "contain" }} />
              <div style={{ fontSize: "10px", lineHeight: "1.55", marginTop: "14px" }}>
                <div style={{ fontWeight: "bold", marginBottom: "2px" }}>For a visit: call your contact person:</div>
                <table style={{ borderCollapse: "collapse" }}>
                  <tbody>
                    <tr>
                      <td style={{ fontWeight: "bold", paddingRight: "14px" }}>Office</td>
                      <td>02/679.00.06</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: "bold", paddingRight: "14px" }}>Florence Bonnave</td>
                      <td>0472/70.30.37</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: "bold", paddingRight: "14px" }}>William Dellavite</td>
                      <td>0472/33.21.24</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: "bold", paddingRight: "14px" }}>Marie Engels</td>
                      <td>0499/31.94.24</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Colonne centre : titre + légende */}
            <div>
              <div style={{ fontSize: "18px", fontWeight: "bold" }}>Apartments For Rent</div>
              <div style={{ fontSize: "13px", fontWeight: "bold", marginTop: "8px" }}>{formatHeaderDate(today)}</div>
              <div style={{ fontSize: "9px", color: "#555", marginTop: "6px" }}>
                (Our apartment listings will be updated every week)
              </div>
              <div style={{ fontSize: "9px", lineHeight: "1.5", color: "#333", marginTop: "12px" }}>
                <div style={{ color: BLUE, fontWeight: "bold" }}>First occupation</div>
                <div style={{ color: RED, fontWeight: "bold", marginBottom: "2px" }}>Promotion/AVAILABLE NOW</div>
                <div><strong>F</strong> = Furnished</div>
                <div><strong>UF</strong> = UnFurnished</div>
                <div>All unfurnished appartments are equipped with lights, curtains (most of them with washing machine, dryer)</div>
                <div>The rent for a cellar is between 30 and 50 euro</div>
                <div>
                  Charges include: <strong>CW</strong> = cold water, <strong>CC</strong> = common charges,{" "}
                  <strong>HW</strong> = hot water, <strong>H</strong> = heating, <strong>Elec</strong> = Electricity, TV,{" "}
                  <strong>Int</strong> = Internet, Insurance = <strong>Ins</strong>
                </div>
              </div>
            </div>
          </div>

          {/* ── Table ── */}
          <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: "10px" }}>
            <colgroup>
              {COL_WIDTHS.map((w, i) => (
                <col key={i} style={{ width: `${w}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {MAIN_HEADERS.map((h) => (
                  <th key={h} className="vth" style={{ border: "1px solid #000" }}>
                    <span>{h}</span>
                  </th>
                ))}
                {/* Gouttière blanche entre table principale et colonnes énergie */}
                <th style={{ border: "none" }} />
                <th className="vth wrap" style={{ border: "1px solid #000" }}>
                  <span>Consommation par m² en kWhEP/m²/an</span>
                </th>
                <th className="vth wrap" style={{ border: "1px solid #000" }}>
                  <span>Classement énergétique</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {groups.map(([projet, items], gi) => (
                <Fragment key={`group-${projet}-${gi}`}>
                  {/* Barre noire de séparation entre projets (sauf avant le 1er) */}
                  {gi > 0 && (
                    <tr>
                      <td colSpan={MAIN_HEADERS.length} style={{ background: "#000", height: "7px", padding: 0 }} />
                      <td style={{ border: "none" }} />
                      <td colSpan={2} style={{ background: "#000", height: "7px", padding: 0 }} />
                    </tr>
                  )}

                  {items.map((bien) => {
                    const avail = formatAvailable(bien.seraDispoPour);
                    // Ligne entière rouge : dispo (NOW) ET vacant depuis > 30 jours.
                    const isPromo = avail.now && daysAvailable(bien.seraDispoPour) > 30;
                    const rowColor = isPromo ? RED : "#000";
                    // Tout ce qui est rouge est en gras (lignes promo + "NOW").
                    const rowWeight = isPromo ? 700 : 400;

                    // Cave / Parking / Garage : logique unifiée (non / incl.(n°) /
                    // oui(montant) / poss(montant)). Le "Rental garage" pointe
                    // désormais sur le vrai champ garage (distinct du parking).
                    const cellarLabel = dispoCell(bien.caveDisponible, bien.caveNumero, bien.caveLoyerMensuel);
                    const parkingLabel = dispoCell(bien.parkingDisponible, bien.parkingNumero, bien.parkingLoyerMensuel);
                    const garageRental = dispoCell(bien.garageDisponible, bien.garageNumero, bien.garageLoyerMensuel);

                    const td: React.CSSProperties = {
                      border: "1px solid #000",
                      height: "30px", // hauteur FIXE identique pour toutes les lignes
                      padding: "2px 4px",
                      whiteSpace: "nowrap",
                      verticalAlign: "middle",
                      textAlign: "center",
                      color: rowColor,
                      fontWeight: rowWeight, // rouge ⇒ gras
                    };
                    // Toutes les cellules centrées (largeur + hauteur).
                    const c: React.CSSProperties = td;
                    const r: React.CSSProperties = td;
                    // Colonnes texte : wrap si le contenu dépasse la largeur fixe (colgroup).
                    const wrap: React.CSSProperties = { ...td, whiteSpace: "normal", wordBreak: "break-word" };

                    return (
                      <tr key={bien.id}>
                        <td style={wrap}>{bien.referenceInterne || "—"}</td>
                        <td style={wrap}>{bien.projet || "—"}</td>
                        <td style={wrap}>{bien.ville || "—"}</td>
                        <td style={c}>{bien.nbChambres || "—"}</td>
                        <td style={c}>{bien.meuble ? "F" : "UF"}</td>
                        <td style={r}>{fPrice(bien.loyerMensuel)}</td>
                        {/* "NOW" en rouge gras ; une date future suit la couleur/graisse de la ligne. */}
                        <td style={{ ...c, color: avail.now ? RED : rowColor, fontWeight: avail.now ? 700 : rowWeight }}>
                          {avail.label}
                        </td>
                        <td style={c}>{bien.etage || "—"}</td>
                        <td style={c}>{bien.nbSdb || "—"}</td>
                        <td style={c}>{bien.typeSol || "—"}</td>
                        <td style={{ ...c, whiteSpace: "normal" }}>{cellarLabel}</td>
                        <td style={{ ...c, whiteSpace: "normal" }}>{parkingLabel}</td>
                        <td style={{ ...c, whiteSpace: "normal" }}>{garageRental}</td>
                        <td style={r}>{fPrice(bien.chargesMensuelles)}</td>
                        <td style={wrap}>{formatCharges(bien.chargesIncluses as string[] | undefined)}</td>
                        <td style={c}>{bien.companyContract ? "Yes" : "No"}</td>
                        <td style={wrap}>{bien.adresse || "—"}</td>
                        {/* Gouttière */}
                        <td style={{ border: "none" }} />
                        {/* Colonnes énergie : toujours noires + jamais en gras (cf. doc). */}
                        <td style={{ ...c, color: "#000", fontWeight: 400 }}>{bien.consoKwh || "—"}</td>
                        <td style={{ ...c, color: "#000", fontWeight: 400 }}>{bien.classePeb || "—"}</td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>

          {/* ── Footer (répété à chaque page à l'impression) ── */}
          <div
            className="mailing-footer"
            style={{
              marginTop: "14px",
              borderTop: "1px solid #ccc",
              paddingTop: "6px",
              textAlign: "center",
              fontSize: "10px",
              color: "#555",
              background: "#fff",
            }}
          >
            <strong>NV/S.A. Skyline Renting Services</strong> - Boulevard du Souverain 292 - 1160 Auderghem - 02/679.00.06
            - TVA BE 0849.827.193 - info@skylinerenting.eu - www.skylinerenting.eu
          </div>
        </div>
      </div>
    </>
  );
}
